import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent, type ReactNode } from 'react';
import { errorMessage } from '../lib/api';
import { shortDate } from '../lib/format';
import type { Comp, CompBody } from '../queries/admin';
import { Badge, Banner, Button, Input, cx } from '../components/ui';

/**
 * Handing out the paid plan, and taking it back. One card, used for a person
 * and for a team, because the decision is the same shape either way: give it,
 * say why, optionally say until when.
 *
 * The grant is not a Stripe subscription and does not pretend to be one. It
 * lives in its own columns on the API side, so nothing Stripe later says
 * about the account can undo it, and the panel always shows both: what Stripe
 * thinks, and what we decided.
 */

/** `<input type="date">` speaks YYYY-MM-DD; the API wants an instant. */
function isoFromDateInput(value: string): string | null {
  if (!value) return null;
  // The end of that day, locally: "until the 31st" should include the 31st.
  const date = new Date(`${value}T23:59:59`);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

function dateInputFromIso(value: string | null): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 10);
}

/** Tomorrow: the earliest expiry that is not already in the past. */
function tomorrow(): string {
  return dateInputFromIso(new Date(Date.now() + 86_400_000).toISOString());
}

/** The grant in a line: who, when, until when, and what they said it was for. */
export function CompSummary({ comp }: { comp: Comp }) {
  return (
    <span className="flex flex-col gap-0.5 text-caption text-chalk-dim">
      <span>
        {comp.active ? 'Comped' : 'Was comped'} {comp.by ? `by ${comp.by}` : ''} on {shortDate(comp.at)}
        {comp.until ? ` · ${comp.active ? 'until' : 'ended'} ${shortDate(comp.until)}` : ' · no expiry'}
      </span>
      {comp.reason ? <span className="text-chalk-faint">“{comp.reason}”</span> : null}
    </span>
  );
}

/** The one-word state of a grant, for a table cell or beside a heading. */
export function CompBadge({ comp }: { comp: Comp | null }) {
  if (!comp) return null;
  return <Badge tone={comp.active ? 'go' : 'neutral'}>{comp.active ? 'Comped' : 'Comp lapsed'}</Badge>;
}

export function CompCard({
  title,
  blurb,
  state,
  comp,
  grant,
  revoke,
  invalidates,
}: {
  title: string;
  /** What comping this thing actually does, in one sentence. */
  blurb: ReactNode;
  /** Where they stand right now, drawn by the caller: it differs per page. */
  state: ReactNode;
  comp: Comp | null;
  grant: (body: CompBody) => Promise<unknown>;
  revoke: () => Promise<unknown>;
  /** Query keys to refetch once the grant changes. */
  invalidates: ReadonlyArray<readonly unknown[]>;
}) {
  const queryClient = useQueryClient();
  const [until, setUntil] = useState(() => dateInputFromIso(comp?.until ?? null));
  const [reason, setReason] = useState(comp?.reason ?? '');
  const [confirming, setConfirming] = useState(false);

  const refresh = () =>
    Promise.all(invalidates.map((queryKey) => queryClient.invalidateQueries({ queryKey: [...queryKey] })));

  const give = useMutation({
    mutationFn: () => grant({ until: isoFromDateInput(until), reason: reason.trim() || null }),
    onSuccess: refresh,
  });
  const take = useMutation({
    mutationFn: revoke,
    onSuccess: () => {
      setConfirming(false);
      setUntil('');
      setReason('');
      return refresh();
    },
  });

  const live = comp?.active ?? false;
  const submit = (event: FormEvent) => {
    event.preventDefault();
    give.mutate();
  };

  return (
    <form onSubmit={submit} className={cx('card px-4 py-4', live && 'border-go/30')}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-h4 text-chalk">{title}</h2>
        <CompBadge comp={comp} />
      </div>
      <div className="mt-1.5 text-body text-chalk-dim">{state}</div>
      {comp ? (
        <div className="mt-1.5">
          <CompSummary comp={comp} />
        </div>
      ) : null}
      <p className="mt-2 text-caption text-chalk-faint">{blurb}</p>

      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-[10rem_1fr_auto] sm:items-end">
        <Input
          label="Expires"
          type="date"
          min={tomorrow()}
          value={until}
          onChange={(event) => setUntil(event.target.value)}
          hint="Blank: never"
        />
        <Input
          label="Reason"
          placeholder="Friends and family, support apology, agreed by email…"
          maxLength={200}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
        <Button type="submit" variant={live ? 'secondary' : 'primary'} busy={give.isPending}>
          {live ? 'Update grant' : 'Grant premium'}
        </Button>
      </div>

      {comp ? (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {confirming ? (
            <>
              <Button size="sm" variant="danger" busy={take.isPending} onClick={() => take.mutate()}>
                Yes, take it back
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setConfirming(false)} disabled={take.isPending}>
                Keep it
              </Button>
            </>
          ) : (
            <Button size="sm" variant="ghost" onClick={() => setConfirming(true)}>
              {live ? 'Revoke' : 'Clear this record'}
            </Button>
          )}
        </div>
      ) : null}

      {give.isError || take.isError ? (
        <div className="mt-3">
          <Banner tone="danger">{errorMessage(give.error ?? take.error)}</Banner>
        </div>
      ) : null}
    </form>
  );
}
